import { useEffect, useMemo, useState } from 'react';
import { WorkflowOrder } from '../../../Api/Orders/ordersApi';
import {
  getUserPreferencesSubject,
  GridPreferences,
  OrdersViewPreferences,
  PrefKey,
  UserPreferencesContent
} from '../../../store/User';
import { getTabs } from '../../../utils/helpers';
import { sameJson } from '../../../utils/sameJson';
import { WORKFLOW_TAB_ORDER } from './workflowTabs';

const ORDER_VIEW_PREFIX = 'order_grid_';
const NO_VIEWS: OrdersSavedView[] = [];

export interface OrdersSavedView {
  name: string;
  key: PrefKey;
  settings: GridPreferences & OrdersViewPreferences;
}

// A save of any key re-emits every view, and a fresh copy of an unchanged one would reset its grid's unsaved layout.
const savedViewsOf = (content: UserPreferencesContent, current: OrdersSavedView[]): OrdersSavedView[] => {
  const views = getTabs(content, ORDER_VIEW_PREFIX, true).map(({ name }) => {
    const key = `${ORDER_VIEW_PREFIX}${name}` as PrefKey;
    const settings = content[key] as OrdersSavedView['settings'];
    const kept = current.find((view) => view.name === name);
    return kept && sameJson(kept.settings, settings) ? kept : { name, key, settings };
  });
  return views.length === current.length && views.every((view, i) => view === current[i]) ? current : views;
};

/** The user's saved views of the Orders grid. */
export const useOrdersSavedViews = (): OrdersSavedView[] => {
  const [views, setViews] = useState<OrdersSavedView[]>([]);

  useEffect(() => {
    const sub = getUserPreferencesSubject().subscribe((preferences) =>
      setViews((current) => savedViewsOf(preferences.content, current))
    );
    return () => sub.unsubscribe();
  }, []);

  return views;
};

/** The workflow tabs of a view, as the page knows them. */
export const viewStages = (view: OrdersSavedView) =>
  WORKFLOW_TAB_ORDER.filter((stage) => view.settings.included_stages?.includes(stage));

/** The open panel of the Orders page: a stage tab, or a saved view, whose panels follow the stage tabs. */
export const useOrdersViewTabs = (enabled: boolean, orders: WorkflowOrder[]) => {
  const savedViews = useOrdersSavedViews();
  const views = enabled ? savedViews : NO_VIEWS;
  const [activeTab, setActiveTab] = useState(0);
  const [selectedViewName, setSelectedViewName] = useState('');
  const viewIndex = views.findIndex((view) => view.name === selectedViewName);
  const selectedView: OrdersSavedView | undefined = views[viewIndex];

  // A view deleted or renamed away while open hands the page back to its stage tab.
  useEffect(() => {
    if (selectedViewName && !selectedView) setSelectedViewName('');
  }, [selectedViewName, selectedView]);

  const rowsByView = useMemo(
    () =>
      new Map(
        views.map((view) => {
          const stages = viewStages(view);
          return [view.key, orders.filter((order) => stages.includes(order.workflow_tab))];
        })
      ),
    [views, orders]
  );

  const selectTab = (index: number) => {
    setSelectedViewName('');
    setActiveTab(index);
  };

  return {
    views,
    activeTab,
    selectedView,
    panelIndex: selectedView ? WORKFLOW_TAB_ORDER.length + viewIndex : activeTab,
    rowsByView,
    selectTab,
    selectView: setSelectedViewName
  };
};
